/**
 * CruiseHeroFrame · BR-MEDIA-005/006 (2026-07-13)
 *
 * The visual scaffold for every Cruise Hero panel across the app.
 *
 * PO directive:
 *   "The Hero should become the signature visual experience of Cruise
 *    Command. Less like a dashboard, more like the opening scene of a
 *    vacation."
 *
 * Composition layers, bottom → top:
 *   1. Base navy skeleton                  — visible while decoding
 *   2. Ship photograph (Ken Burns slow zoom, focal-point aware)
 *   3. Environmental mood wash             — Caribbean teal / Alaska
 *                                            glacier / Med gold / etc.
 *   4. Cinematic warm colour grading       — subtle amber cast (~14%)
 *   5. Left-side legibility gradient       — protects the panel
 *   6. Bottom-anchored legibility gradient — protects meta / CTA
 *   7. Content children in Safe Zone
 *   8. Environmental mood chip             — top-right, region label
 *   9. Quality provenance chip             — bottom-right
 *
 * Cruise Design System · Hero Safe Zones
 * ─────────────────────────────────────────────────────────────────
 *   Desktop (≥1024px)  panel:  left 0-46% × top 0-100%
 *                      meta:   left 0-46% × bottom 12-32%
 *                      cta:    left 0-46% × bottom  0-14%
 *   Tablet (≥640px)    panel:  left 0-58%
 *   Mobile (<640px)    panel:  bottom 45% (stacked below imagery)
 *                      ship must remain visible in top 55%
 *
 * Motion
 *   • 480ms fade-in on image decode.
 *   • Very slow Ken Burns (60s ease-in-out, 1.000 → 1.020 scale peak,
 *     translate drifts by 0.25% × 0.10%). Luxury-hotel subtle;
 *     atmospheric, not animated. Disabled entirely under
 *     `prefers-reduced-motion: reduce`.
 *
 * Composition (PO 2026-07-14 · zoom-out pass)
 *   • Image wrapper extends 14% above and below the frame so
 *     `object-fit: cover` reveals ~15-20% more of the source hero
 *     (more ocean, more wake, more sky) without stretching.
 *   • Default focal_point is {x: 0.62, y: 0.55} — the ship stays
 *     slightly right of centre with the left-side safe zone
 *     preserved.
 *
 * Traveler vs Editor
 *   • Traveler surfaces (Dashboard, Journey, Cruise Detail, future
 *     Ship/Port/Country/Region/Destination pages) render no
 *     provenance chip. AI provenance stays in CKB Studio only.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, ShieldCheck } from "lucide-react";

const QUALITY_STYLE = {
  1: { label: "Official photography", tone: "text-emerald-100",  Icon: ShieldCheck },
  2: { label: "Editorial",             tone: "text-emerald-100", Icon: ShieldCheck },
  3: { label: "Curated stock",         tone: "text-white/80",    Icon: Sparkles },
  // PO 2026-07-14 · Traveler-facing surfaces no longer render an
  // "AI-generated" pill. Provenance lives in CKB Studio / editorial
  // tools; the traveler Hero should simply be beautiful.
  4: { label: "",                      tone: "text-white/70",    Icon: Sparkles },
  5: { label: "",                      tone: "text-white/60",    Icon: Sparkles },
};

const DEFAULT_MOOD = {
  key: "default", label: "Ocean", primary: "#3EA1FF", secondary: "#D4AF37",
};

const clamp01 = (v) => (typeof v === "number" && v >= 0 && v <= 1 ? v : 0.5);

function HeroImage({ src, alt, focal, testId, kenBurns }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  // PO 2026-07-14 · Composition — the image lives inside a wrapper
  // that is intentionally taller than the frame so `object-cover`
  // has more vertical room to fill. The result reveals ~15-20% more
  // of the original AI hero (more ocean, more wake, more sky) while
  // preserving the left-side safe zone and keeping the ship slightly
  // right of centre. No stretching, no distortion.
  const x = Math.round(clamp01(focal?.x ?? 0.62) * 100);
  const y = Math.round(clamp01(focal?.y ?? 0.55) * 100);
  return (
    <div className="absolute inset-x-0 pointer-events-none" style={{ top: "-14%", bottom: "-14%" }}>
      <img
        key={src}
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        data-testid={testId}
        data-loaded={loaded ? "true" : "false"}
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover ${kenBurns ? "cc-hero-kenburns" : ""}`}
        style={{
          opacity: loaded ? 1 : 0,
          transition: "opacity 480ms ease-out",
          objectPosition: `${x}% ${y}%`,
        }}
      />
    </div>
  );
}

/**
 * CruiseHeroFrame
 *
 * @param {object}   hero        Resolved backend hero object (see
 *                               media_resolver). Reads: image_url,
 *                               alt, focal_point, quality,
 *                               environmental_mood, source.
 * @param {string}   backendBase Optional REACT_APP_BACKEND_URL.
 * @param {ReactNode} children   Content rendered inside the safe zone.
 * @param {number}   minHeight   Minimum hero height (desktop).
 * @param {ReactNode} placeholder Rendered when hero has no image yet.
 * @param {string}   testId
 */
export default function CruiseHeroFrame({
  hero,
  backendBase = "",
  children,
  minHeight = 420,
  placeholder = null,
  testId = "cruise-hero-frame",
}) {
  const rawUrl = hero?.image_url || null;
  const src = rawUrl
    ? (rawUrl.startsWith("http") ? rawUrl : `${(backendBase || "").replace(/\/$/, "")}${rawUrl}`)
    : null;
  const quality = hero?.quality || {};
  const qStyle = QUALITY_STYLE[quality.tier] || QUALITY_STYLE[5];
  const mood = { ...DEFAULT_MOOD, ...(hero?.environmental_mood || {}) };

  return (
    <section
      data-testid={testId}
      data-hero-state={src ? "image" : "loading"}
      data-hero-tier={quality.tier || 5}
      data-hero-mood={mood.key}
      className="cc-fade-up relative rounded-[24px] overflow-hidden mb-5 shadow-[0_25px_55px_-15px_rgba(20,58,117,0.4)]"
      style={{ minHeight, background: "#0a1628" }}
    >
      {/* 1 · Base navy skeleton (always present, prevents any FOUC) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg,#0a1628 0%,#0f2237 55%,#14304a 100%)",
        }}
      />

      {/* 2 · Ship photograph — Ken Burns slow zoom, focal-point aware */}
      {src && (
        <HeroImage
          src={src}
          alt={hero?.alt || "Cruise hero"}
          focal={hero?.focal_point}
          testId={`${testId}-img`}
          kenBurns
        />
      )}
      {!src && placeholder}

      {/* 3 · Environmental mood wash — subtle tint that ties the
             image to the itinerary's region. Never overpowers the
             photograph. Uses `mix-blend-color` at low opacity. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `radial-gradient(circle at 78% 30%, ${mood.primary}22 0%, transparent 65%),
             radial-gradient(circle at 24% 80%, ${mood.secondary}18 0%, transparent 60%)`,
          mixBlendMode: "color",
        }}
      />

      {/* 4 · Cinematic warm grading — pulls every image into the
             Cruise Command palette without overwhelming. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-soft-light"
        style={{
          background:
            "linear-gradient(135deg, rgba(212,175,55,0.24) 0%, rgba(30,109,229,0.12) 55%, rgba(6,14,34,0.34) 100%)",
        }}
      />

      {/* 5 · Left-side legibility wash — protects the identity panel */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(6,14,34,0.62) 0%, rgba(6,14,34,0.30) 34%, rgba(0,0,0,0) 58%)",
        }}
      />

      {/* 6 · Bottom-anchored legibility wash */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: "42%",
          background:
            "linear-gradient(180deg, rgba(6,14,34,0) 0%, rgba(6,14,34,0.36) 60%, rgba(6,14,34,0.62) 100%)",
        }}
      />

      {/* 7 · Content children — panel content lives inside the safe zone. */}
      <div className="cc-hero-safe-zone">
        {children}
      </div>

      {/* 8 · Environmental mood chip — quiet region label top-right */}
      {mood.label && mood.key !== "default" && (
        <div
          data-testid={`${testId}-mood`}
          data-mood={mood.key}
          className="absolute top-3 right-3 lg:top-4 lg:right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-white/85"
          style={{
            background: "rgba(6,14,34,0.42)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontSize: 10.5,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          <span
            aria-hidden
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: mood.primary, boxShadow: `0 0 8px ${mood.primary}` }}
          />
          {mood.label}
        </div>
      )}

      {/* 9 · Quality provenance chip — bottom-right */}
      {qStyle.label && (
        <div
          data-testid={`${testId}-quality`}
          data-tier={quality.tier}
          className={`absolute bottom-3 right-3 lg:bottom-4 lg:right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md ${qStyle.tone}`}
          style={{
            background: "rgba(6,14,34,0.42)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontSize: 10.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
          title={quality.label || undefined}
        >
          <qStyle.Icon className="w-3 h-3" />
          {qStyle.label}
        </div>
      )}

      {/* Component-scoped CSS — safe zones + Ken Burns motion +
          reduced-motion respect. */}
      <style>{`
        .cc-hero-safe-zone {
          position: relative;
          width: 100%;
          height: 100%;
          min-height: ${minHeight}px;
        }
        @keyframes cc-hero-kenburns-anim {
          /* PO 2026-07-14 · Motion — the traveler should feel
             atmosphere, not animation. Very slow, very subtle,
             almost imperceptible. Luxury-hotel register. */
          0%   { transform: scale(1.000) translate(0%, 0%); }
          50%  { transform: scale(1.020) translate(-0.25%, -0.10%); }
          100% { transform: scale(1.000) translate(0%, 0%); }
        }
        .cc-hero-kenburns {
          animation: cc-hero-kenburns-anim 60s ease-in-out infinite;
          will-change: transform;
        }
        @media (prefers-reduced-motion: reduce) {
          .cc-hero-kenburns { animation: none !important; }
        }
      `}</style>
    </section>
  );
}

export { HeroImage };
